import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tabs,
  Tab,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import PauseIcon from '@material-ui/icons/Pause';
import VisibilityIcon from '@material-ui/icons/Visibility';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import { cronjobManagePermission } from '../../permissions';

interface EnvConfig {
  key: string;
  displayName: string;
  namespace: string;
  color: string;
}

interface CronJobInfo {
  name: string;
  schedule: string;
  suspended: boolean;
  lastScheduleTime: string | null;
  activeJobs: number;
  createdAt: string | null;
}

interface JobInfo {
  name: string;
  startTime: string | null;
  completionTime: string | null;
  duration: string | null;
  succeeded: number;
  failed: number;
  active: number;
  conditions: { type: string; status: string; reason: string }[];
}

const useStyles = makeStyles(theme => ({
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 40,
    '& .MuiTab-root': {
      minHeight: 40,
      fontWeight: 700,
      textTransform: 'uppercase',
      fontSize: '0.75rem',
      letterSpacing: '0.06em',
    },
  },
  table: {
    '& th': {
      fontWeight: 600,
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: theme.palette.text.secondary,
      borderBottom: `2px solid ${theme.palette.divider}`,
      padding: theme.spacing(1, 2),
    },
    '& td': {
      padding: theme.spacing(1.5, 2),
    },
  },
  subTable: {
    '& th': {
      fontWeight: 600,
      fontSize: '0.7rem',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: theme.palette.text.secondary,
      borderBottom: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(0.75, 2),
    },
    '& td': {
      padding: theme.spacing(1, 2),
      fontSize: '0.8rem',
    },
  },
  empty: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  cronName: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    fontWeight: 600,
  },
  schedule: {
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  expandRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  historyContainer: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(1, 2, 2, 2),
  },
  logContainer: {
    backgroundColor: '#1e1e1e',
    color: '#d4d4d4',
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    lineHeight: 1.6,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    height: 400,
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
  successText: {
    color: '#4caf50',
    fontSize: '0.75rem',
    marginTop: 4,
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: '0.75rem',
    marginTop: 4,
  },
}));

function formatRelativeTime(isoDate: string | null): string {
  if (!isoDate) return '—';
  const d = new Date(isoDate);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "à l'instant";
  if (mins < 60) return `il y a ${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days}j`;
}

function getJobStatus(job: JobInfo): { label: string; color: string } {
  if (job.active > 0) return { label: 'En cours', color: '#1976d2' };
  if (job.succeeded > 0) return { label: 'Réussi', color: '#4caf50' };
  if (job.failed > 0) return { label: 'Échoué', color: '#f44336' };
  return { label: 'Inconnu', color: '#9e9e9e' };
}

export const CronjobManagerContent = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const backendBaseUrl = configApi.getString('backend.baseUrl');

  const annotations = entity.metadata.annotations ?? {};
  const labelSelector = annotations['backstage.io/kubernetes-label-selector'];

  const { allowed: canManage } = usePermission({ permission: cronjobManagePermission });

  const [activeTab, setActiveTab] = useState(0);
  const [envs, setEnvs] = useState<EnvConfig[]>([]);
  const [cronJobs, setCronJobs] = useState<Record<string, CronJobInfo[]>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<Record<string, string | undefined>>({});

  // Expand state per cronjob
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [history, setHistory] = useState<Record<string, JobInfo[]>>({});
  const [historyLoading, setHistoryLoading] = useState<Record<string, boolean>>({});

  // Trigger dialog
  const [triggerDialog, setTriggerDialog] = useState<{ open: boolean; cronJobName?: string; envKey?: string }>({ open: false });
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [triggerResult, setTriggerResult] = useState<{ success?: string; error?: string }>({});

  // Suspend loading
  const [suspendLoading, setSuspendLoading] = useState<Record<string, boolean>>({});

  // Logs dialog
  const [logsDialog, setLogsDialog] = useState<{ open: boolean; cronJobName?: string; jobName?: string; namespace?: string }>({ open: false });
  const [logsContent, setLogsContent] = useState('');
  const [logsPodName, setLogsPodName] = useState('');
  const [logsLoading, setLogsLoading] = useState(false);

  useEffect(() => {
    try {
      const arr = configApi.getConfigArray('fondative.environments');
      setEnvs(
        arr.map(c => ({
          key: c.getString('key'),
          displayName: c.getString('displayName'),
          namespace: c.getOptionalString('namespace') ?? 'default',
          color: c.getOptionalString('color') ?? '#1976d2',
        })),
      );
    } catch {
      setEnvs([]);
    }
  }, [configApi]);

  const fetchCronJobs = useCallback(
    async (env: EnvConfig) => {
      setLoading(prev => ({ ...prev, [env.key]: true }));
      setError(prev => ({ ...prev, [env.key]: undefined }));
      try {
        const params = new URLSearchParams({ namespace: env.namespace });
        if (labelSelector) params.set('labelSelector', labelSelector);
        const res = await fetchApi.fetch(
          `${backendBaseUrl}/api/cronjob-manager/cronjobs?${params}`,
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }
        const data = await res.json();
        setCronJobs(prev => ({ ...prev, [env.key]: data.items }));
      } catch (err: any) {
        setError(prev => ({ ...prev, [env.key]: err.message }));
        setCronJobs(prev => ({ ...prev, [env.key]: [] }));
      } finally {
        setLoading(prev => ({ ...prev, [env.key]: false }));
      }
    },
    [backendBaseUrl, fetchApi, labelSelector],
  );

  useEffect(() => {
    if (!envs.length) return;
    envs.forEach(env => fetchCronJobs(env));
  }, [envs, fetchCronJobs]);

  const toggleExpand = useCallback(
    async (cronJobName: string, namespace: string) => {
      const key = `${namespace}/${cronJobName}`;
      const isExpanded = expanded[key];
      setExpanded(prev => ({ ...prev, [key]: !isExpanded }));

      if (!isExpanded && !history[key]) {
        setHistoryLoading(prev => ({ ...prev, [key]: true }));
        try {
          const params = new URLSearchParams({ namespace });
          const res = await fetchApi.fetch(
            `${backendBaseUrl}/api/cronjob-manager/cronjobs/${encodeURIComponent(cronJobName)}/history?${params}`,
          );
          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body.error ?? `HTTP ${res.status}`);
          }
          const data = await res.json();
          setHistory(prev => ({ ...prev, [key]: data.items }));
        } catch {
          setHistory(prev => ({ ...prev, [key]: [] }));
        } finally {
          setHistoryLoading(prev => ({ ...prev, [key]: false }));
        }
      }
    },
    [expanded, history, backendBaseUrl, fetchApi],
  );

  const handleTrigger = useCallback(
    async () => {
      const { cronJobName, envKey } = triggerDialog;
      const env = envs.find(e => e.key === envKey);
      if (!cronJobName || !env) return;
      setTriggerLoading(true);
      setTriggerResult({});
      try {
        const params = new URLSearchParams({ namespace: env.namespace });
        const res = await fetchApi.fetch(
          `${backendBaseUrl}/api/cronjob-manager/cronjobs/${encodeURIComponent(cronJobName)}/trigger?${params}`,
          { method: 'POST' },
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }
        const data = await res.json();
        setTriggerResult({ success: `Job ${data.jobName} créé` });
        setTriggerDialog({ open: false });
        // Refresh cronjobs and clear history cache
        fetchCronJobs(env);
        const histKey = `${env.namespace}/${cronJobName}`;
        setHistory(prev => {
          const next = { ...prev };
          delete next[histKey];
          return next;
        });
      } catch (err: any) {
        setTriggerResult({ error: err.message });
      } finally {
        setTriggerLoading(false);
      }
    },
    [triggerDialog, envs, backendBaseUrl, fetchApi, fetchCronJobs],
  );

  const handleSuspend = useCallback(
    async (cronJobName: string, envKey: string, suspend: boolean) => {
      const env = envs.find(e => e.key === envKey);
      if (!env) return;
      const key = `${env.namespace}/${cronJobName}`;
      setSuspendLoading(prev => ({ ...prev, [key]: true }));
      try {
        const params = new URLSearchParams({ namespace: env.namespace });
        const res = await fetchApi.fetch(
          `${backendBaseUrl}/api/cronjob-manager/cronjobs/${encodeURIComponent(cronJobName)}/suspend?${params}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ suspend }),
          },
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }
        // Refresh cronjobs
        fetchCronJobs(env);
      } catch (err: any) {
        // Silently fail, user sees no change
      } finally {
        setSuspendLoading(prev => ({ ...prev, [key]: false }));
      }
    },
    [envs, backendBaseUrl, fetchApi, fetchCronJobs],
  );

  const openLogs = useCallback(
    async (cronJobName: string, jobName: string, namespace: string) => {
      setLogsDialog({ open: true, cronJobName, jobName, namespace });
      setLogsContent('');
      setLogsPodName('');
      setLogsLoading(true);
      try {
        const params = new URLSearchParams({ namespace, jobName });
        const res = await fetchApi.fetch(
          `${backendBaseUrl}/api/cronjob-manager/cronjobs/${encodeURIComponent(cronJobName)}/logs?${params}`,
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }
        const data = await res.json();
        setLogsContent(data.logs);
        setLogsPodName(data.podName);
      } catch (err: any) {
        setLogsContent(`Erreur: ${err.message}`);
      } finally {
        setLogsLoading(false);
      }
    },
    [backendBaseUrl, fetchApi],
  );

  if (!labelSelector) {
    return (
      <Box p={3}>
        <Typography color="textSecondary">
          Ajoutez l'annotation <code>backstage.io/kubernetes-label-selector</code> pour activer la gestion des CronJobs.
        </Typography>
      </Box>
    );
  }

  if (!envs.length) return null;

  const currentEnv = envs[activeTab];
  const currentCronJobs = cronJobs[currentEnv.key] ?? [];
  const isLoading = loading[currentEnv.key] ?? true;
  const currentError = error[currentEnv.key];

  return (
    <>
      <Card>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          className={classes.tabs}
          indicatorColor="primary"
          textColor="primary"
        >
          {envs.map(env => (
            <Tab key={env.key} label={env.displayName} style={{ color: env.color }} />
          ))}
        </Tabs>
        <CardContent style={{ padding: 0 }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : currentError ? (
            <Box className={classes.empty}>
              <Typography color="error">{currentError}</Typography>
            </Box>
          ) : currentCronJobs.length === 0 ? (
            <Box className={classes.empty}>
              <Typography color="textSecondary">Aucun CronJob trouvé</Typography>
            </Box>
          ) : (
            <Table className={classes.table} size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: 40 }} />
                  <TableCell>Nom</TableCell>
                  <TableCell>Schedule</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Dernière exécution</TableCell>
                  {canManage && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {currentCronJobs.map(cj => {
                  const expandKey = `${currentEnv.namespace}/${cj.name}`;
                  const isExpanded = expanded[expandKey] ?? false;
                  const jobHistory = history[expandKey] ?? [];
                  const isHistoryLoading = historyLoading[expandKey] ?? false;
                  const isSuspendLoading = suspendLoading[expandKey] ?? false;

                  return (
                    <>
                      <TableRow
                        key={cj.name}
                        className={classes.expandRow}
                        onClick={() => toggleExpand(cj.name, currentEnv.namespace)}
                      >
                        <TableCell>
                          <IconButton size="small">
                            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography className={classes.cronName}>{cj.name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography className={classes.schedule}>{cj.schedule}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={cj.suspended ? 'Suspendu' : 'Actif'}
                            style={{
                              backgroundColor: cj.suspended ? '#ff9800' : '#4caf50',
                              color: '#fff',
                              fontSize: '0.65rem',
                              height: 20,
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {formatRelativeTime(cj.lastScheduleTime)}
                          </Typography>
                        </TableCell>
                        {canManage && (
                          <TableCell align="right" onClick={e => e.stopPropagation()}>
                            <Box display="flex" justifyContent="flex-end" style={{ gap: 4 }}>
                              <Tooltip title="Déclencher" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setTriggerDialog({ open: true, cronJobName: cj.name, envKey: currentEnv.key })
                                  }
                                >
                                  <PlayArrowIcon fontSize="small" style={{ color: '#1976d2' }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={cj.suspended ? 'Réactiver' : 'Suspendre'} arrow>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={isSuspendLoading}
                                    onClick={() => handleSuspend(cj.name, currentEnv.key, !cj.suspended)}
                                  >
                                    {isSuspendLoading ? (
                                      <CircularProgress size={16} />
                                    ) : cj.suspended ? (
                                      <PlayArrowIcon fontSize="small" style={{ color: '#4caf50' }} />
                                    ) : (
                                      <PauseIcon fontSize="small" style={{ color: '#ff9800' }} />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                      <TableRow key={`${cj.name}-expand`}>
                        <TableCell colSpan={canManage ? 6 : 5} style={{ padding: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box className={classes.historyContainer}>
                              <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                                Historique des Jobs
                              </Typography>
                              {isHistoryLoading ? (
                                <Box display="flex" justifyContent="center" p={2}>
                                  <CircularProgress size={24} />
                                </Box>
                              ) : jobHistory.length === 0 ? (
                                <Typography variant="body2" color="textSecondary">
                                  Aucun job trouvé
                                </Typography>
                              ) : (
                                <Table className={classes.subTable} size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Nom du Job</TableCell>
                                      <TableCell>Début</TableCell>
                                      <TableCell>Durée</TableCell>
                                      <TableCell>Statut</TableCell>
                                      <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {jobHistory.map(job => {
                                      const status = getJobStatus(job);
                                      return (
                                        <TableRow key={job.name} hover>
                                          <TableCell>
                                            <Typography style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                              {job.name}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="body2" color="textSecondary">
                                              {job.startTime
                                                ? new Date(job.startTime).toLocaleString('fr-FR')
                                                : '—'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="body2" color="textSecondary">
                                              {job.duration ?? '—'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Chip
                                              size="small"
                                              label={status.label}
                                              style={{
                                                backgroundColor: status.color,
                                                color: '#fff',
                                                fontSize: '0.6rem',
                                                height: 18,
                                                fontWeight: 600,
                                              }}
                                            />
                                          </TableCell>
                                          <TableCell align="right">
                                            <Tooltip title="Voir les logs" arrow>
                                              <IconButton
                                                size="small"
                                                onClick={() =>
                                                  openLogs(cj.name, job.name, currentEnv.namespace)
                                                }
                                              >
                                                <VisibilityIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Trigger confirmation dialog */}
      <Dialog open={triggerDialog.open} onClose={() => setTriggerDialog({ open: false })}>
        <DialogTitle>Déclencher le CronJob</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Voulez-vous déclencher manuellement le CronJob{' '}
            <strong>{triggerDialog.cronJobName}</strong> sur{' '}
            <strong>{envs.find(e => e.key === triggerDialog.envKey)?.displayName}</strong> ?
          </DialogContentText>
          {triggerResult.success && (
            <Typography className={classes.successText}>{triggerResult.success}</Typography>
          )}
          {triggerResult.error && (
            <Typography className={classes.errorText}>{triggerResult.error}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTriggerDialog({ open: false })} disabled={triggerLoading}>
            Annuler
          </Button>
          <Button color="primary" onClick={handleTrigger} disabled={triggerLoading}>
            {triggerLoading ? <CircularProgress size={18} /> : 'Déclencher'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Logs dialog */}
      <Dialog
        open={logsDialog.open}
        onClose={() => setLogsDialog({ open: false })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Typography variant="h6">Logs</Typography>
            <Typography
              style={{ fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: 600, color: '#9e9e9e' }}
            >
              {logsDialog.jobName}
              {logsPodName ? ` (${logsPodName})` : ''}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {logsLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <pre className={classes.logContainer}>{logsContent}</pre>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogsDialog({ open: false })}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
